import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import { exportRegister } from './register-export.js';
import {
  ApiError,
  VARIATION_CAUSES,
  createVariation,
  getCosts,
  listVariations,
  moveVariation,
  updateVariation,
  type CostEntry,
  type Variation,
  type VariationStatus,
  type VariationView,
} from '../api.js';
import { daysAgo, formatMoney, moneyForInput } from '../labels.js';
import { AppLink } from '../link.js';
import { navigate } from '../router.js';
import { CrossLink, focusClass, useFocusRow } from './cross-links.js';
// No `sectionHref`: a variation is never deleted — it is withdrawn or rejected,
// and it stays on its own page saying so — so no move leaves the reader on an
// address that names nothing.
import { recordHref } from './project-sections.js';
import { RecordBack, RecordMissing, RecordPage } from './record-page.js';
import {
  Drawer,
  RegisterToolbar,
  RowActions,
  matches,
  useRegisterView,
  type RowAction,
} from './register-kit.js';
import { LOAD_ALL_LIMIT } from '../api/paging.js';

/**
 * Variations (CM-04) — journey J7.
 *
 * The register where fit-out contractors lose money, so the screen is built
 * around the one number nobody wants to say out loud:
 *
 *   **work already instructed that nobody has agreed to pay for.**
 *
 * It sits at the top, in words and in money, because on a real job it
 * accumulates one "just get on with it" at a time and is never totalled until
 * the final account. A register that merely LISTED variations would leave that
 * addition to a human at the worst possible moment.
 *
 * WHAT A CLIENT SEES
 * ------------------
 * `04` gives a Client/Consultant `variation: g('VA')` — they approve one and
 * must never see its cost or margin. The server omits those fields entirely
 * (ADR-005), so this screen has nothing to hide: it renders what it was given.
 * The exposure block is money end to end and is absent for them too.
 *
 * **The export follows the same rule.** Its money columns are built from the
 * same `view.exposure` test the table uses, so a caller without financial
 * visibility gets a file with those columns ABSENT rather than blank. A blank
 * money column in a spreadsheet is a number somebody will read as zero.
 *
 * ON THE REGISTER PATTERN
 * -----------------------
 * The row's one action is the next step in the variation's own life — start
 * pricing, send the quote, approve, instruct, and so on down the chain.
 * Rejecting and withdrawing are endings that need a written reason and both
 * stay in the overflow menu.
 */

const STATUS_LABELS: Readonly<Record<VariationStatus, string>> = {
  potential_change: 'Possible change',
  under_review: 'Being priced',
  quotation_submitted: 'Quoted, awaiting a decision',
  approved: 'Approved',
  rejected: 'Rejected',
  instructed: 'Instructed',
  executed: 'Done',
  billed: 'Invoiced',
  collected: 'Paid',
  withdrawn: 'Withdrawn',
};

/** What the button SAYS, which is not the state's name. */
const MOVE_LABELS: Readonly<Record<VariationStatus, string>> = {
  potential_change: 'Reopen',
  under_review: 'Start pricing',
  quotation_submitted: 'Send the quote',
  approved: 'Approve',
  rejected: 'Reject',
  instructed: 'Instruct the work',
  executed: 'Mark done',
  billed: 'Invoice it',
  collected: 'Mark paid',
  withdrawn: 'Withdraw',
};

const CAUSE_LABELS: Readonly<Record<string, string>> = {
  client_request: 'Client asked for it',
  design_development: 'Design developed',
  site_condition: 'Found on site',
  authority_requirement: 'Authority required it',
  contractor_error: 'Our error',
  other: 'Other',
};

/** Never colour alone — the symbol and the word both carry the state. */
const STATUS_SYMBOLS: Readonly<Record<VariationStatus, string>> = {
  potential_change: '○',
  under_review: '◐',
  quotation_submitted: '◐',
  approved: '✓',
  rejected: '✗',
  instructed: '▶',
  executed: '▶',
  billed: '✓',
  collected: '✓',
  withdrawn: '—',
};

/** The moves that need a written reason — the machine demands it too. */
const NEEDS_REASON: readonly VariationStatus[] = ['withdrawn', 'rejected'];

/**
 * The lifecycle forwards, for picking the row's one action.
 *
 * `rejected` and `withdrawn` are absent: both are endings, both demand a
 * written reason, and a register whose one-click action rejects a variation is
 * a register that rejects variations by accident.
 */
const FORWARD: readonly VariationStatus[] = [
  'under_review',
  'quotation_submitted',
  'approved',
  'instructed',
  'executed',
  'billed',
  'collected',
];

/** Where a variation has got to, in buckets somebody would actually ask for. */
const PRICING: readonly VariationStatus[] = ['potential_change', 'under_review'];
const AGREED: readonly VariationStatus[] = [
  'approved',
  'instructed',
  'executed',
  'billed',
  'collected',
];
const CLOSED: readonly VariationStatus[] = ['rejected', 'withdrawn'];

interface Props {
  readonly projectId: string;
  readonly currency: string;
  /**
   * One variation, as its own page, from
   * `/projects/<id>/commercial/variations/<recordId>`.
   *
   * A variation EARNS a page for one reason above the others: the pair of
   * numbers that makes this register worth having — instructed without approval,
   * and already being spent against — is a VALUE on the record and a LIST of
   * costs beside it. In a drawer the list was below the fold, so the half of the
   * argument that actually alarms somebody was the half they had to scroll for.
   */
  readonly recordId?: string | null;
  readonly can: {
    readonly write: boolean;
    readonly edit: boolean;
    /** `variation: X`. Gates the button; the rows are already filtered. */
    readonly exportRows: boolean;
  };
}

/**
 * What the drawer is doing. `null` means it is shut.
 *
 * `detail` is gone: the variation has a page and an address now (§4.3). The
 * forms stay, `reason` above all — rejecting or withdrawing a variation is a
 * commercial decision that goes on the record with words behind it.
 */
type DrawerMode =
  | { readonly kind: 'edit'; readonly id: string }
  | { readonly kind: 'reason'; readonly id: string; readonly to: VariationStatus }
  | { readonly kind: 'add' };

export function Variations({ projectId, currency, recordId = null, can }: Props): JSX.Element {
  const [view, setView] = useState<VariationView | null>(null);
  /** Whether this screen has asked the server for every variation. */
  const [wantAll, setWantAll] = useState(false);
  /** Not the same as an empty register — see `Drawings.tsx`. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState<DrawerMode | null>(null);
  /** Set by the form inside the drawer, so closing it can ask before discarding. */
  const [formDirty, setFormDirty] = useState(false);
  const register = useRegisterView('number');
  /**
   * The cost entries, to show what has actually been spent against each
   * variation. Silent on failure: the whole cost report is behind
   * `finance:view`, and a caller without it loses this line rather than the
   * register.
   */
  const [costs, setCosts] = useState<readonly CostEntry[]>([]);

  const load = useCallback(async () => {
    try {
      setView(await listVariations(projectId, wantAll ? LOAD_ALL_LIMIT : undefined));
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the variations.');
    }
    try {
      setCosts((await getCosts(projectId)).costs);
    } catch {
      setCosts([]);
    }
  }, [projectId, wantAll]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<VariationView>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setView(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Shut the drawer, and forget whether its form had edits in it. */
  const closeDrawer = useCallback(() => {
    setDrawer(null);
    setFormDirty(false);
    setError(null);
  }, []);

  const variations = view?.variations;

  /**
   * The rows on screen.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps` — which this repo treats as an
   * error. It runs before the early returns below because hooks must.
   */
  const shown = useMemo(() => {
    if (!variations) return [];

    const stage = register.chosen.stage;
    const cause = register.chosen.cause;

    const filtered = variations.filter((v) => {
      // Everything somebody would type: the number quoted in the email, what
      // the change is, and why it happened.
      const hay = [v.number, v.title, v.description, CAUSE_LABELS[v.cause] ?? v.cause]
        .filter(Boolean)
        .join(' ');
      if (!matches(hay, register.query)) return false;
      if (cause && v.cause !== cause) return false;
      if (stage === 'exposed' && !v.instructed_without_approval) return false;
      if (stage === 'pricing' && !PRICING.includes(v.status)) return false;
      if (stage === 'awaiting' && v.status !== 'quotation_submitted') return false;
      if (stage === 'agreed' && !AGREED.includes(v.status)) return false;
      if (stage === 'closed' && !CLOSED.includes(v.status)) return false;
      return true;
    });

    const money = (value: string | null | undefined): number =>
      value === null || value === undefined ? Number.NEGATIVE_INFINITY : Number(value);

    const compare: Readonly<Record<string, (a: Variation, b: Variation) => number>> = {
      number: (a, b) => a.number.localeCompare(b.number),
      // Longest exposed first — the register's whole point, worst at the top.
      exposed: (a, b) => (b.exposed_days ?? -1) - (a.exposed_days ?? -1),
      // A variation whose value this caller cannot see sorts last rather than
      // first: an omitted figure is not a small one.
      value: (a, b) => money(b.value_impact) - money(a.value_impact),
    };

    return [...filtered].sort(compare[register.sortKey] ?? compare.number!);
  }, [variations, register.query, register.chosen.stage, register.chosen.cause, register.sortKey]);

  /** The record the drawer is about. `null` for the add form, which has none. */
  const subject =
    drawer && drawer.kind !== 'add'
      ? (variations?.find((v) => v.id === drawer.id) ?? null)
      : null;

  /** Make a move, asking for a reason first where the machine demands one. */
  function move(variation: Variation, to: VariationStatus): void {
    setError(null);
    if (NEEDS_REASON.includes(to)) {
      setDrawer({ kind: 'reason', id: variation.id, to });
      return;
    }
    void run(() => moveVariation(projectId, variation.id, { status: to }));
  }

  /** The one move that goes on the row. */
  function primaryFor(variation: Variation): RowAction {
    const forward = FORWARD.find((status) => variation.allowed_next.includes(status));
    if (forward) {
      return {
        label: MOVE_LABELS[forward],
        disabled: busy,
        onSelect: () => move(variation, forward),
      };
    }
    return {
      label: 'Open',
      onSelect: () => navigate(recordHref(projectId, 'variations', variation.id)),
    };
  }

  /**
   * Everything that is not the one visible action.
   *
   * The same list serves the row and the record page. "Open" comes off on the
   * page, for the usual reason: you are already looking at the record.
   */
  function overflowFor(
    variation: Variation,
    primary: RowAction | null,
    onPage = false,
  ): readonly RowAction[] {
    const actions: RowAction[] = onPage
      ? []
      : [
          {
            label: 'Open',
            onSelect: () => navigate(recordHref(projectId, 'variations', variation.id)),
          },
        ];
    if (can.edit) {
      actions.push({
        label: 'Edit',
        onSelect: () => setDrawer({ kind: 'edit', id: variation.id }),
      });
    }
    // The register never offers a move the machine has not returned, so a
    // button here cannot be one the service then refuses.
    for (const to of variation.allowed_next) {
      actions.push({
        label: MOVE_LABELS[to],
        danger: to === 'withdrawn' || to === 'rejected',
        disabled: busy,
        onSelect: () => move(variation, to),
      });
    }
    // Whatever is already on the row must not also be in the menu behind it.
    return primary ? actions.filter((action) => action.label !== primary.label) : actions;
  }

  async function exportRows(): Promise<void> {
    const showsMoney = Boolean(view?.exposure);
    const headers = [
      'Variation',
      'Title',
      'Cause',
      'Where it is',
      ...(showsMoney ? [`Value (${currency})`, 'Margin', 'Margin from the figures'] : []),
      'Instructed without approval',
      'Days exposed',
      'Days on the programme',
      'Description',
    ];
    const rows = shown.map((v) => [
      v.number,
      v.title,
      CAUSE_LABELS[v.cause] ?? v.cause,
      STATUS_LABELS[v.status],
      // ABSENT, not blank, for a caller the server gave no money to (ADR-005).
      ...(showsMoney
        ? [v.value_impact ?? '', v.margin_impact ?? '', v.margin_from_figures ?? '']
        : []),
      v.instructed_without_approval ? 'Yes' : '',
      v.exposed_days !== null ? String(v.exposed_days) : '',
      v.time_impact_days !== null ? String(v.time_impact_days) : '',
      v.description ?? '',
    ]);
    setError(null);
    try {
      // Recorded BEFORE the file is written, and refusable — `09` §Threats
      // wants the row count and the filters. See `register-export.ts`.
      await exportRegister({
        projectId,
        resource: 'variation',
        filename: `variations-${projectId.slice(0, 8)}.csv`,
        headers,
        rows,
        filters: { query: register.query, ...register.chosen },
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not export.');
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Variations</h3>
      {view && can.write && (
        <button type="button" className="inline" onClick={() => setDrawer({ kind: 'add' })}>
          Raise a variation
        </button>
      )}
    </div>
  );

  /*
   * The same four states on a record page, still four different sentences. The
   * furniture above them changes: "Raise a variation" is the wrong offer to
   * somebody who followed a link to one particular variation.
   */
  const above = recordId ? <RecordBack projectId={projectId} register="variations" /> : head;

  if (denied) {
    return (
      <>
        {above}
        <p className="hint">Your role does not include this project&rsquo;s variations.</p>
      </>
    );
  }

  if (!view) {
    return (
      <>
        {above}
        {error ? (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        ) : (
          <p aria-live="polite">Loading {recordId ? 'the variation' : 'the variations'}…</p>
        )}
      </>
    );
  }

  /**
   * The three forms, shared by the register and the record page.
   *
   * `ReasonForm` above all: rejecting or withdrawing a variation is a commercial
   * decision, and the words behind it go on the record. A page with its own copy
   * is a second place for that to drift.
   */
  const drawers = (
    <>
      {drawer?.kind === 'add' && (
        <Drawer title="Raise a variation" dirty={formDirty} onClose={closeDrawer}>
          <VariationForm
            currency={currency}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => createVariation(projectId, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'edit' && subject && (
        <Drawer
          title={`Edit ${subject.number}`}
          subtitle={subject.title}
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <VariationForm
            existing={subject}
            currency={currency}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              // The version this form was OPENED on. Two people pricing one VO
              // is the case optimistic concurrency was built for: without this,
              // the second save quietly overwrites the first one's cost.
              if (
                await run(() =>
                  updateVariation(projectId, subject.id, input, subject.record_version),
                )
              )
                closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'reason' && subject && (
        <Drawer
          title={`${MOVE_LABELS[drawer.to]} — ${subject.number}`}
          subtitle={subject.title}
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <ReasonForm
            to={drawer.to}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (reason) => {
              const to = drawer.to;
              if (await run(() => moveVariation(projectId, subject.id, { status: to, reason }))) {
                closeDrawer();
              }
            }}
          />
        </Drawer>
      )}
    </>
  );

  const alert = error && (
    <div className="alert" role="alert">
      <span>{error}</span>
    </div>
  );

  // ----------------------------------------------------- one variation, in full
  if (recordId) {
    const record = view.variations.find((v) => v.id === recordId) ?? null;
    if (!record) {
      /* A FAILED LOAD IS NOT A MISSING RECORD — see `Materials.tsx`. */
      return error ? (
        <>
          {above}
          {alert}
        </>
      ) : (
        <RecordMissing projectId={projectId} register="variations" />
      );
    }

    const forward = primaryFor(record);
    const primary = forward.label === 'Open' ? null : forward;
    /* NO BAR WHEN THERE IS NOTHING IN IT — see `Drawings.tsx`. */
    const more = overflowFor(record, primary, true);
    const actions =
      primary || more.length > 0 ? (
        <RowActions rowLabel={record.number} {...(primary ? { primary } : {})} more={more} />
      ) : null;

    return (
      <>
        {alert}
        <RecordPage
          projectId={projectId}
          register="variations"
          reference={record.number}
          title={record.title}
          status={STATUS_LABELS[record.status]}
          meta={
            <>
              {CAUSE_LABELS[record.cause] ?? record.cause}
              {/*
                THE SENTENCE THIS REGISTER EXISTS FOR, on the head where it
                cannot be scrolled past. `finance:view` decides whether the
                figures are on the record at all — the server OMITS them (ADR-005)
                — but the exposure is not a figure and everybody who can read the
                variation can read it.
              */}
              {record.instructed_without_approval && (
                <span className="task-overdue">
                  {' · '}
                  Instructed without approval
                  {record.exposed_days !== null &&
                    ` — ${record.exposed_days} ${record.exposed_days === 1 ? 'day' : 'days'} ago`}
                </span>
              )}
            </>
          }
          {...(actions ? { actions } : {})}
          properties={
            <VariationProperties
              variation={record}
              currency={currency}
              /* The exposure banner is present exactly when the server sent
                 figures, which is the same `showsMoney` test the table uses. */
              showsMoney={Boolean(view.exposure)}
            />
          }
        >
          {/*
            WHAT HAS ACTUALLY BEEN SPENT AGAINST IT. A variation with a value and
            no spend is normal; a variation instructed without approval that is
            ALREADY being spent against is the whole point of the register, and
            it is this list beside the flag above that shows it. In a drawer this
            was below the fold.
          */}
          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Spent against it</h4>
            </div>
            <SpentAgainst
              projectId={projectId}
              number={record.number}
              costs={costs}
              bare
              /*
               * An empty state has to name an action ITS reader can take. A
               * caller without `finance:view` never receives the cost report at
               * all, so "nothing recorded" would be a claim this screen cannot
               * make to them — it says what was matched on instead.
               */
              empty={
                <p className="hint">
                  No cost on this project carries <strong>{record.number}</strong> as its
                  reference. That reference is what links a cost to a variation — there is no
                  other link between them — so a QS types it in when recording the cost.
                </p>
              }
            />
          </div>
        </RecordPage>
        {drawers}
      </>
    );
  }

  return (
    <>
      {head}

      {alert}

      {view.exposure && <ExposureBanner exposure={view.exposure} currency={currency} />}

      {view.variations.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No variations yet.</strong>
          </p>
          <p>
            {can.write
              ? 'Every change to the scope, from the moment somebody mentions it. Raising one early is what makes it billable later.'
              : /*
                 * An empty state must name an action ITS READER can take. A
                 * Client/Consultant holds `variation: VA` — they approve one,
                 * they never raise one.
                 */
                'Nobody has raised one on this project. The QS or the Project Manager records every change to the scope, from the moment somebody mentions it.'}
          </p>
        </div>
      ) : (
        <>
          <RegisterToolbar
            view={register}
            searchLabel="Search variations"
            searchPlaceholder="Number, what changed, why"
            shown={shown.length}
            total={view.variations.length}
            registerTotal={view.total}
            onLoadAll={() => setWantAll(true)}
            loadingAll={wantAll && view.variations.length < view.total}
            noun={['variation', 'variations']}
            filters={[
              {
                key: 'stage',
                label: 'Show',
                options: [
                  { value: 'exposed', label: 'Instructed without approval' },
                  { value: 'awaiting', label: 'Awaiting a decision' },
                  { value: 'pricing', label: 'Being priced' },
                  { value: 'agreed', label: 'Agreed' },
                  { value: 'closed', label: 'Rejected or withdrawn' },
                ],
              },
              {
                key: 'cause',
                label: 'Cause',
                options: VARIATION_CAUSES.map((c) => ({
                  value: c,
                  label: CAUSE_LABELS[c] ?? c,
                })),
              },
            ]}
            sorts={[
              { key: 'number', label: 'Reference' },
              { key: 'exposed', label: 'Longest exposed' },
              ...(view.exposure ? [{ key: 'value', label: 'Largest value' }] : []),
            ]}
          >
            {can.exportRows && (
              <button type="button" className="secondary inline" onClick={() => void exportRows()}>
                Export {shown.length === view.variations.length ? 'all' : 'these'} to CSV
              </button>
            )}
          </RegisterToolbar>

          {shown.length === 0 ? (
            /*
             * A DIFFERENT empty state from "no variations yet". This reader has
             * a register full of them and a filter that excludes all of them.
             */
            <div className="empty">
              <p>
                <strong>No variation matches that.</strong>
              </p>
              <p>
                <button type="button" className="link-button" onClick={register.clear}>
                  Clear the search and filters
                </button>{' '}
                to see all {view.variations.length}.
              </p>
            </div>
          ) : (
            <div className={`rk-scroll${register.density === 'compact' ? ' rk-compact' : ''}`}>
              <table className="grid rk-cards">
                <caption className="visually-hidden">
                  Variations, with what they are worth and where they have got to.{' '}
                  {shown.length} of {view.variations.length} shown.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Variation</th>
                    <th scope="col">Cause</th>
                    {view.exposure && <th scope="col">Value ({currency})</th>}
                    {view.exposure && <th scope="col">Margin</th>}
                    <th scope="col">Where it is</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((v) => {
                    const primary = primaryFor(v);
                    return (
                      <VariationRow key={v.id} variation={v}>
                        <td>
                          <span className="ms-symbol" aria-hidden="true">
                            {STATUS_SYMBOLS[v.status]}
                          </span>{' '}
                          {/*
                            A REAL LINK, because the record has an address now —
                            so a middle-click, a new tab and a copied link all
                            mean what they look like they mean.
                          */}
                          <AppLink
                            className="rk-open"
                            to={recordHref(projectId, 'variations', v.id)}
                          >
                            <strong>{v.number}</strong> {v.title}
                            <span className="visually-hidden"> — open this variation</span>
                          </AppLink>
                          {/*
                            The thing the register exists to surface, on the row
                            itself and in words, at BOTH densities. A colour
                            alone would not survive a printout, and this is the
                            line somebody needs to see in a meeting.
                          */}
                          {v.instructed_without_approval && (
                            <span className="task-overdue">
                              {/*
                                The separator, INSIDE the span. Without it the
                                title ran straight into this — "the
                                receptionInstructed without approval".
                              */}
                              {' · '}
                              Instructed without approval
                              {v.exposed_days !== null && ` · ${daysAgo(v.exposed_days)}`}
                            </span>
                          )}
                          {/*
                            SECONDARY. Hidden at compact density, and all of it
                            is on the record's drawer as well.
                          */}
                          <span className="rk-secondary">
                            {v.description && <span className="snag-note">{v.description}</span>}
                            <SpentAgainst projectId={projectId} number={v.number} costs={costs} />
                            {v.time_impact_days !== null && v.time_impact_days !== 0 && (
                              // `snag-note` rather than `tree-code`: these stack
                              // as their own lines. Inline, the exposure warning
                              // and the programme impact ran together into one
                              // unreadable string.
                              <span className="snag-note">
                                {v.time_impact_days > 0 ? '+' : ''}
                                {v.time_impact_days} days to the programme
                              </span>
                            )}
                          </span>
                        </td>
                        <td data-label="Cause">{CAUSE_LABELS[v.cause] ?? v.cause}</td>
                        {view.exposure && (
                          <td data-label="Value">{formatMoney(v.value_impact ?? null)}</td>
                        )}
                        {view.exposure && (
                          <td data-label="Margin">
                            {v.margin_impact ? (
                              formatMoney(v.margin_impact)
                            ) : v.margin_from_figures ? (
                              // A different claim, and it says so. `07`: cost,
                              // value and margin are never derived from one
                              // another, because a variation can be sold at cost
                              // or at a loss and carry margin the two headline
                              // figures do not show.
                              <span className="hint">
                                {formatMoney(v.margin_from_figures)}
                                <span className="tree-code">{' · '}from the figures</span>
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        <td data-label="Where it is">{STATUS_LABELS[v.status]}</td>
                        <td>
                          <RowActions
                            rowLabel={v.number}
                            primary={primary}
                            more={overflowFor(v, primary)}
                          />
                        </td>
                      </VariationRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {drawers}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * What the variation IS — the record page's rail.
 *
 * Everything compact density hides is here. The money is governed by the same
 * `showsMoney` test the table uses — a caller the server gave no figures to sees
 * no money row at all, rather than a row saying nothing. The one LIST, what has
 * actually been spent against it, is in the main column, beside rather than
 * under the flag that says it was instructed without approval.
 */
function VariationProperties({
  variation,
  currency,
  showsMoney,
}: {
  readonly variation: Variation;
  readonly currency: string;
  readonly showsMoney: boolean;
}): JSX.Element {
  return (
    <>
      <dl className="detail-grid">
        <dt>Where it is</dt>
        <dd>{STATUS_LABELS[variation.status]}</dd>
        <dt>Cause</dt>
        <dd>{CAUSE_LABELS[variation.cause] ?? variation.cause}</dd>
        {showsMoney && (
          <>
            <dt>Value ({currency})</dt>
            <dd>{formatMoney(variation.value_impact ?? null)}</dd>
            <dt>Margin</dt>
            <dd>
              {variation.margin_impact ? (
                formatMoney(variation.margin_impact)
              ) : variation.margin_from_figures ? (
                <span className="hint">
                  {formatMoney(variation.margin_from_figures)}
                  <span className="tree-code">{' · '}from the figures</span>
                </span>
              ) : (
                '—'
              )}
            </dd>
          </>
        )}
        {variation.time_impact_days !== null && variation.time_impact_days !== 0 && (
          <>
            <dt>Programme</dt>
            <dd>
              {variation.time_impact_days > 0 ? '+' : ''}
              {variation.time_impact_days} days
            </dd>
          </>
        )}
        {variation.description && (
          <>
            <dt>What changed</dt>
            <dd>{variation.description}</dd>
          </>
        )}
      </dl>
    </>
  );
}


// ---------------------------------------------------------------------------

/**
 * What has actually been spent against a variation.
 *
 * THE LINK IS THE REFERENCE, AND THE WORDS SAY SO. There is no foreign key
 * between a variation and a cost entry, and inventing one in the browser would
 * be a claim the database cannot back. What exists is the cost entry's
 * `reference` — the field a QS types the VO number into precisely so the two
 * can be reconciled later — so this matches on it and says "recorded against
 * VO-0003", which is exactly what was matched.
 *
 * A variation with a value and no spend is normal. A variation instructed
 * without approval that is ALREADY being spent against is the register's whole
 * point, and it is the pair of numbers that shows it.
 */
function SpentAgainst({
  projectId,
  number,
  costs,
  /**
   * Drop the leading words, for a caller whose own heading already says them.
   *
   * On the row this line has to introduce itself — it sits among other secondary
   * detail. Under a section headed "Spent against it" on the record page it
   * would read as two headings for one list.
   */
  bare = false,
  /**
   * What to say when nothing matched, for a caller that wants a section rather
   * than silence. On the ROW, silence is right: an empty line under every
   * variation without a cost would be noise on the register.
   */
  empty = null,
}: {
  readonly projectId: string;
  readonly number: string;
  readonly costs: readonly CostEntry[];
  readonly bare?: boolean;
  readonly empty?: JSX.Element | null;
}): JSX.Element | null {
  const matching = costs.filter((c) => c.reference?.trim() === number);
  if (matching.length === 0) return empty;

  return (
    <span className="snag-note">
      {!bare && <>Recorded against {number}: </>}
      {matching.map((c, index) => (
        <span key={c.id}>
          {index > 0 && ', '}
          <CrossLink
            projectId={projectId}
            section="commercial"
            focus={c.id}
            says={`a cost recorded with this variation's number as its reference`}
          >
            {formatMoney(c.amount)}
          </CrossLink>
        </span>
      ))}
    </span>
  );
}

/** One row, which knows whether a cross-link came looking for it. */
function VariationRow({
  variation,
  children,
}: {
  readonly variation: Variation;
  readonly children: React.ReactNode;
}): JSX.Element {
  const { focused, ref } = useFocusRow<HTMLTableRowElement>(variation.id);
  return (
    <tr
      ref={ref}
      className={focusClass(
        focused,
        `vo-${variation.status}${variation.instructed_without_approval ? ' vo-exposed' : ''}`,
      )}
      aria-current={focused ? 'true' : undefined}
    >
      {children}
    </tr>
  );
}

// ---------------------------------------------------------------------------

/**
 * The number the whole register is for.
 *
 * Shown even when it is zero — "nothing instructed without approval" is worth
 * saying on a job where it is true, and a banner that only appears when things
 * are bad teaches people that its absence means nobody has looked.
 */
function ExposureBanner({
  exposure,
  currency,
}: {
  readonly exposure: NonNullable<VariationView['exposure']>;
  readonly currency: string;
}): JSX.Element {
  const pct = exposure.unapproved_pct_of_contract;
  const level = pct === null ? 'unknown' : pct > 5 ? 'red' : pct > 2 ? 'amber' : 'green';

  return (
    <div className={`panel exposure exposure-${level}`} role="status">
      {exposure.unapproved_count === 0 ? (
        <p>
          <strong>Nothing has been instructed without approval.</strong> Every variation being
          built has been agreed.
        </p>
      ) : (
        <>
          <p>
            {/*
              The COST, not the value. This is the money that has left the
              business if the client never approves — the margin was never
              earned, so putting it here would inflate the figure by our own
              markup. `05` §7, and the note on `Exposure.unapproved_cost`.
            */}
            <strong>
              {currency} {formatMoney(exposure.unapproved_cost)} has been spent on work
              instructed without approval
            </strong>{' '}
            — {exposure.unapproved_count}{' '}
            {exposure.unapproved_count === 1 ? 'variation' : 'variations'}
            {pct !== null && `, ${pct}% of the contract`}.
          </p>
          <p className="hint">
            That is what it cost us. It would have been billed at {currency}{' '}
            {formatMoney(exposure.unapproved_value)} — which nobody has agreed to pay. It is the
            number to take to the client meeting.
            {pct === null &&
              ' This project has no contract value, so it cannot be read as a share of one.'}
          </p>
          {/*
            Said loudly, because the figure above is WRONG-LOW without it. Since
            the exposure became the cost (ADR-036), a variation raised with only
            a price contributes nothing — and an under-reported exposure is the
            one number on this screen that must never be quietly optimistic.
          */}
          {exposure.unapproved_unpriced_count > 0 && (
            <p className="alert" role="alert">
              <strong>
                {exposure.unapproved_unpriced_count}{' '}
                {exposure.unapproved_unpriced_count === 1
                  ? 'of these has no cost recorded'
                  : 'of these have no cost recorded'}
                , so the figure above is lower than the truth.
              </strong>{' '}
              Put a cost against{' '}
              {exposure.unapproved_unpriced_count === 1 ? 'it' : 'them'} to see what is really at
              risk.
            </p>
          )}
        </>
      )}

      {exposure.approved_count > 0 && (
        <p className="hint">
          Separately, {currency} {formatMoney(exposure.approved_value)} is approved and not yet
          invoiced.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface FormValues {
  title: string;
  description: string | null;
  cause: string;
  cost_impact: string | null;
  value_impact: string | null;
  margin_impact: string | null;
  time_impact_days: number | null;
}

function VariationForm({
  existing,
  currency,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: {
  readonly existing?: Variation;
  readonly currency: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: FormValues) => void | Promise<void>;
}): JSX.Element {
  const [title, setTitle] = useState(existing?.title ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [cause, setCause] = useState(existing?.cause ?? 'client_request');
  const [cost, setCost] = useState(moneyForInput(existing?.cost_impact));
  const [value, setValue] = useState(moneyForInput(existing?.value_impact));
  const [margin, setMargin] = useState(moneyForInput(existing?.margin_impact));
  const [days, setDays] = useState(
    existing?.time_impact_days === null || existing?.time_impact_days === undefined
      ? ''
      : String(existing.time_impact_days),
  );

  /*
   * Compared against the record rather than tracked with a touched flag:
   * somebody who types a character and deletes it has changed nothing. The
   * cause starts at a real value, so it is compared against that.
   */
  useEffect(() => {
    const startingDays =
      existing?.time_impact_days === null || existing?.time_impact_days === undefined
        ? ''
        : String(existing.time_impact_days);
    onDirtyChange?.(
      title !== (existing?.title ?? '') ||
        description !== (existing?.description ?? '') ||
        cause !== (existing?.cause ?? 'client_request') ||
        cost !== (existing?.cost_impact ?? '') ||
        value !== (existing?.value_impact ?? '') ||
        margin !== (existing?.margin_impact ?? '') ||
        days !== startingDays,
    );
  }, [title, description, cause, cost, value, margin, days, existing, onDirtyChange]);

  /**
   * Whether the caller may touch money at all.
   *
   * The server OMITS the field rather than nulling it, so its absence from an
   * existing record is the signal — and on a new one there is nothing to read,
   * so the fields are offered and the server refuses if they may not.
   */
  const showsMoney = existing === undefined || 'cost_impact' in existing;

  const money = (v: string) => v.replace(/,/g, '').trim();
  const valid = (v: string) => v === '' || /^\d+(\.\d{1,4})?$/.test(money(v));
  const canSave = title.trim().length > 0 && valid(cost) && valid(value) && valid(margin);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!canSave) return;
    void onSubmit({
      title: title.trim(),
      description: description.trim() || null,
      cause,
      // Sent as STRINGS all the way to numeric(18,4). A JSON number would
      // round-trip through a float on the way (07 §DI2).
      cost_impact: money(cost) || null,
      value_impact: money(value) || null,
      margin_impact: money(margin) || null,
      time_impact_days: days.trim() === '' ? null : Number(days),
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="field">
        <label htmlFor="vo-title">What is the change?</label>
        <input
          id="vo-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={300}
          disabled={busy}
          placeholder="Extra power to the server room"
        />
      </div>

      <div className="field">
        <label htmlFor="vo-description">Any detail</label>
        <textarea
          id="vo-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={4000}
          disabled={busy}
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="vo-cause">Why is it happening?</label>
          <select
            id="vo-cause"
            value={cause}
            onChange={(e) => setCause(e.target.value)}
            disabled={busy}
          >
            {VARIATION_CAUSES.map((key) => (
              <option key={key} value={key}>
                {CAUSE_LABELS[key] ?? key}
              </option>
            ))}
          </select>
          <p className="hint">
            The most argued-about field on the job, and the reason this register is worth keeping.
          </p>
        </div>
        <div className="field">
          <label htmlFor="vo-days">Days added to the programme</label>
          <input
            id="vo-days"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            inputMode="numeric"
            disabled={busy}
            placeholder="0"
          />
        </div>
      </div>

      {showsMoney && (
        <div className="field-row">
          <div className="field">
            <label htmlFor="vo-cost">What it costs us ({currency})</label>
            <input
              id="vo-cost"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              inputMode="decimal"
              disabled={busy}
              placeholder="30000.00"
            />
          </div>
          <div className="field">
            <label htmlFor="vo-value">What we charge ({currency})</label>
            <input
              id="vo-value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              inputMode="decimal"
              disabled={busy}
              placeholder="40000.00"
            />
          </div>
        </div>
      )}

      {showsMoney && (
        <div className="field">
          <label htmlFor="vo-margin">Margin ({currency})</label>
          <input
            id="vo-margin"
            value={margin}
            onChange={(e) => setMargin(e.target.value)}
            inputMode="decimal"
            disabled={busy}
            placeholder="Leave blank to show the difference"
          />
          <p className="hint">
            Its own figure, not the subtraction. A variation can be sold at cost, at a loss, or
            carry margin the other two do not show — preliminaries used up, a delay pushing other
            work into overtime. Left blank, the screen shows the difference and says that is what
            it is.
          </p>
        </div>
      )}

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !canSave}>
          {busy ? 'Saving…' : existing ? 'Save' : 'Raise it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

const MIN_REASON = 20;

/** No `variation`: the drawer's heading already says which one, and which move. */
function ReasonForm({
  to,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: {
  readonly to: VariationStatus;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (reason: string) => void | Promise<void>;
}): JSX.Element {
  const [reason, setReason] = useState('');

  useEffect(() => {
    onDirtyChange?.(reason !== '');
  }, [reason, onDirtyChange]);

  return (
    <div className="panel">
      <p className="hint">
        {to === 'withdrawn'
          ? 'A withdrawn variation keeps its number, because it has been quoted in emails. The record has to say why it was dropped.'
          : 'A rejection is a commercial position, and the reason is what gets quoted back months later.'}
      </p>

      <div className="field">
        <label htmlFor="vo-reason">Why?</label>
        <textarea
          id="vo-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={2000}
          disabled={busy}
          placeholder="Client withdrew the request before we priced it."
        />
        <p className="hint">
          {reason.trim().length}/{MIN_REASON} characters minimum.
        </p>
      </div>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || reason.trim().length < MIN_REASON}
          onClick={() => void onSubmit(reason.trim())}
        >
          {busy ? 'Saving…' : MOVE_LABELS[to]}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
